import React, { useState, useEffect } from 'react';
import { Calendar, Tag, ExternalLink, TrendingUp, X } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { newsArticles } from '../mock/mockData';
import usePageTitle from '../hooks/usePageTitle';
import axios from 'axios';

const News = () => {
  usePageTitle('Steel Industry News');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [articles, setArticles] = useState(newsArticles);
  const [isLive, setIsLive] = useState(false);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/news`);
        if (res.data?.articles?.length >= 3) {
          setArticles(res.data.articles);
          setIsLive(true);
        }
      } catch (err) {
        // keep static fallback articles
      }
    };
    fetchNews();
  }, []);

  const categories = ['all', 'Market Update', 'Industry News', 'Stock Market', 'Global Market'];

  const filteredArticles = selectedCategory === 'all' 
    ? articles 
    : articles.filter(article => article.category === selectedCategory);

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div data-testid="news-live-badge" className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6 ${isLive ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
              {isLive ? (
                <>
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
                  </span>
                  <span>Live Feed — Updated Automatically</span>
                </>
              ) : (
                <>
                  <TrendingUp className="w-4 h-4" />
                  <span>Latest Updates</span>
                </>
              )}
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
              Steel Industry <span className="text-orange-600">News & Insights</span>
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Stay updated with the latest developments in India's steel sector and global market trends
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white border-b border-slate-200 sticky top-[120px] z-40">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === category
                    ? 'bg-orange-600 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {category === 'all' ? 'All News' : category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* News Grid */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          {filteredArticles.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredArticles.map((article) => (
                <Card key={article.id} className="group hover:shadow-xl transition-all duration-300 border-slate-200 overflow-hidden">
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4 bg-orange-600 text-white px-3 py-1 rounded-full text-xs font-medium">
                      {article.category}
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(article.date)}</span>
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3 line-clamp-2 group-hover:text-orange-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-slate-600 mb-4 line-clamp-3">{article.excerpt}</p>
                    <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <Tag className="w-3 h-3" />
                        {article.source}
                      </span>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        data-testid={`news-read-more-${article.id}`}
                        className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                        onClick={() => setSelectedArticle(article)}
                      >
                        Read More <ExternalLink className="ml-1 w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">📰</div>
              <h3 className="text-2xl font-bold text-slate-900 mb-2">No Articles Found</h3>
              <p className="text-slate-600">Try selecting a different category</p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Subscription */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Subscribe to Weekly Steel Insights</h2>
            <p className="text-lg text-slate-600 mb-8">
              Get the latest steel industry news, market trends, and price updates delivered to your inbox every week
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-orange-600"
              />
              <Button className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-3">
                Subscribe
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-4">
              Weekly updates • Unsubscribe anytime • No spam, guaranteed
            </p>
          </div>
        </div>
      </section>

      {/* Article Modal */}
      {selectedArticle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedArticle(null)}>
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="relative">
              <img src={selectedArticle.image} alt={selectedArticle.title} className="w-full h-64 object-cover rounded-t-2xl" />
              <button 
                onClick={() => setSelectedArticle(null)}
                className="absolute top-4 right-4 bg-white rounded-full p-2 shadow-lg hover:bg-slate-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="absolute top-4 left-4 bg-orange-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                {selectedArticle.category}
              </div>
            </div>
            <div className="p-8">
              <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(selectedArticle.date)}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Tag className="w-4 h-4" />
                  <span>{selectedArticle.source}</span>
                </div>
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">{selectedArticle.title}</h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-slate-700 leading-relaxed mb-4">{selectedArticle.excerpt}</p>
                {!selectedArticle.url && (
                  <>
                    <p className="text-slate-700 leading-relaxed mb-4">
                      This comprehensive analysis provides insights into the current steel market trends and their implications for the industry. 
                      Industry experts continue to monitor these developments closely as they impact both domestic and international markets.
                    </p>
                    <p className="text-slate-700 leading-relaxed mb-4">
                      The steel sector remains a critical component of India's infrastructure growth story, with major players adapting to changing 
                      market dynamics and consumer demands. Recent government initiatives and policy changes have further shaped the industry landscape.
                    </p>
                  </>
                )}
                {selectedArticle.url && (
                  <a
                    href={selectedArticle.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="news-full-article-link"
                    className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-medium px-6 py-3 rounded-lg transition-colors mb-4 no-underline"
                  >
                    Read Full Article at {selectedArticle.source} <ExternalLink className="w-4 h-4" />
                  </a>
                )}
                <div className="mt-6 p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <p className="text-sm text-orange-800">
                    <strong>Source:</strong> {selectedArticle.source} • This article provides key insights into current market conditions 
                    and industry developments affecting steel trading and manufacturing sectors.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default News;
